// Paysafe.js single-use tokens have a ~15min TTL; 10min threshold leaves
// the order pipeline a 5min margin to reach Paysafe before expiry. ECOMM-9911.
import { PAY_ONLINE_MERRCO } from 'src/payments/constants';
import { PAYSAFE_TOKEN_FRESHNESS_CHECK_FLAG } from 'src/state/actions/cart';

export const PAYSAFE_TOKEN_FRESHNESS_MS = 10 * 60 * 1000;

export const PAYSAFE_TOKEN_EXPIRED_MESSAGE =
  'Your card information has expired. Please re-enter your card details to continue.';

export function isPaysafeTokenStale(tokenizedAt: number | null | undefined, nowMs: number = Date.now()): boolean {
  if (tokenizedAt == null) return true;
  return nowMs - tokenizedAt > PAYSAFE_TOKEN_FRESHNESS_MS;
}

type CartPaysafeInputHolder = {
  paysafeInput: { token?: string | null; zip?: string | null; tokenizedAt?: number | null };
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export function clearPaysafeInput(Cart: CartPaysafeInputHolder): void {
  Cart.paysafeInput = { token: null, zip: null, tokenizedAt: null };
}

type FreshnessCheckCart = CartPaysafeInputHolder & {
  paymentMethod: string | null | undefined;
  dispensary?: { id?: string | null } | null;
};

type FreshnessCheckLogger = {
  warn: (message: string, context?: Record<string, unknown>) => void;
};

type FreshnessCheckArgs = {
  Cart: FreshnessCheckCart;
  flags: Record<string, boolean | undefined> | null | undefined;
  addErrors: (errors: Array<Record<string, unknown>>) => void;
  logger: FreshnessCheckLogger;
};

// Call after the submit flow's saveMethods iteration. Returns `{ error: true }`
// when the token is stale (form was unmounted, autosave couldn't refresh);
// returns null when submit should proceed.
export function checkPaysafeTokenFreshness({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Cart,
  flags,
  addErrors,
  logger,
}: FreshnessCheckArgs): { error: true } | null {
  const enabled = flags?.[PAYSAFE_TOKEN_FRESHNESS_CHECK_FLAG] ?? false;
  if (!enabled) return null;
  if (Cart.paymentMethod !== PAY_ONLINE_MERRCO) return null;

  // If no token exists yet (customer hasn't filled card details), let downstream
  // validation handle the missing-token case rather than showing a misleading
  // "card expired" message on a first-time checkout.
  if (Cart.paysafeInput.token == null) return null;

  const { tokenizedAt } = Cart.paysafeInput;
  const nowMs = Date.now();
  if (!isPaysafeTokenStale(tokenizedAt, nowMs)) return null;

  logger.warn('paysafe token stale at submit; form likely unmounted before submit', {
    dispensaryId: Cart.dispensary?.id,
    tokenizedAt,
    ageMs: tokenizedAt != null ? nowMs - tokenizedAt : null,
  });

  // Clear so a retry doesn't reuse the stale token.
  clearPaysafeInput(Cart);
  // Use only `card: 'payment'` (not `path: 'payment'`) — both fields cause the message
  // to render twice (top banner + inline). The inline render is contextually correct
  // since the user is being asked to re-enter card details visible in that section.
  addErrors([{ card: 'payment', message: PAYSAFE_TOKEN_EXPIRED_MESSAGE }]);
  return { error: true };
}
